import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  wrapper: {
    width: "100%",
    maxWidth: 760
  },
  card: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap"
  },
  statusConnected: {
    color: theme.palette.success.main,
    fontWeight: 700
  },
  statusDisconnected: {
    color: theme.palette.error.main,
    fontWeight: 700
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  field: {
    minWidth: 260,
    flex: 1
  }
}));

const GoogleCalendar = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    connected: false,
    calendarId: "primary",
    syncDirection: "infochat_to_google",
    googleAccountEmail: ""
  });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/google-calendar/settings");
      setSettings({
        enabled: Boolean(data.enabled),
        connected: Boolean(data.connected),
        calendarId: data.calendarId || "primary",
        syncDirection: data.syncDirection || "infochat_to_google",
        googleAccountEmail: data.googleAccountEmail || ""
      });
    } catch (err) {
      toast.error("Erro ao carregar configuração do Google Agenda.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();

    const params = new URLSearchParams(window.location.search);
    const googleCalendarStatus = params.get("googleCalendar");

    if (googleCalendarStatus === "connected") {
      toast.success("Google Agenda conectado com sucesso.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (googleCalendarStatus === "denied") {
      toast.warning("Conexão com Google Agenda cancelada.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (googleCalendarStatus === "error") {
      const reason = params.get("reason");

      if (reason === "state_expired") {
        toast.error(
          "A conexao com o Google Agenda expirou. Clique em Conectar Google novamente."
        );
      } else {
        toast.error("Erro ao concluir conexao com o Google Agenda.");
      }

      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleChange = event => {
    const { name, value, checked, type } = event.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/google-calendar/settings", {
        enabled: settings.enabled,
        calendarId: settings.calendarId,
        syncDirection: settings.syncDirection
      });

      setSettings(prev => ({ ...prev, ...data }));
      toast.success("Configuração do Google Agenda salva.");
    } catch (err) {
      toast.error("Erro ao salvar configuração do Google Agenda.");
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    setSaving(true);
    try {
      const { data } = await api.get("/google-calendar/auth-url");
      window.location.href = data.authUrl;
    } catch (err) {
      toast.error("Erro ao iniciar conexão com o Google Agenda.");
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setSaving(true);
    try {
      const { data } = await api.delete("/google-calendar/disconnect");
      setSettings(prev => ({ ...prev, ...data }));
      toast.success("Google Agenda desconectado.");
    } catch (err) {
      toast.error("Erro ao desconectar Google Agenda.");
    } finally {
      setSaving(false);
    }
  };

  const handleImportEvents = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/google-calendar/import");
      toast.success(
        `Importação concluída. Importados: ${data.imported || 0}. Atualizados: ${data.updated || 0}. Ignorados: ${data.skipped || 0}.`
      );
      await fetchSettings();
    } catch (err) {
      toast.error("Erro ao importar eventos do Google Agenda.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <CircularProgress size={24} />;
  }

  return (
    <div className={classes.wrapper}>
      <Paper className={classes.card} elevation={1}>
        <Typography variant="h6">Google Agenda</Typography>
        <Typography variant="body2" color="textSecondary">
          Conecte uma conta Google para preparar a sincronização dos agendamentos
          do INFOCHAT com o Google Agenda.
        </Typography>

        <div className={classes.row}>
          <Typography>Status:</Typography>
          <Typography
            className={
              settings.connected
                ? classes.statusConnected
                : classes.statusDisconnected
            }
          >
            {settings.connected ? "Conectado" : "Desconectado"}
          </Typography>
          {settings.googleAccountEmail && (
            <Typography color="textSecondary">
              Conta: {settings.googleAccountEmail}
            </Typography>
          )}
        </div>

        <div className={classes.row}>
          <Typography>Ativar integração</Typography>
          <Switch
            color="primary"
            name="enabled"
            checked={settings.enabled}
            onChange={handleChange}
          />
        </div>

        <div className={classes.row}>
          <TextField
            className={classes.field}
            label="Calendário"
            name="calendarId"
            value={settings.calendarId}
            onChange={handleChange}
            helperText='Use "primary" para a agenda principal da conta Google.'
            variant="outlined"
            size="small"
          />

          <FormControl variant="outlined" size="small" className={classes.field}>
            <InputLabel id="google-calendar-sync-direction-label">
              Sentido da sincronização
            </InputLabel>
            <Select
              labelId="google-calendar-sync-direction-label"
              label="Sentido da sincronização"
              name="syncDirection"
              value={settings.syncDirection}
              onChange={handleChange}
            >
              <MenuItem value="infochat_to_google">
                INFOCHAT para Google
              </MenuItem>
              <MenuItem value="google_to_infochat">
                Google para INFOCHAT
              </MenuItem>
              <MenuItem value="two_way">Sincronização dupla</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            Salvar configuração
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleConnect}
            disabled={saving}
          >
            Conectar Google
          </Button>
          {settings.connected && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDisconnect}
              disabled={saving}
            >
              Desconectar
            </Button>
          )}
          {settings.connected && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleImportEvents}
              disabled={saving || !settings.enabled}
            >
              Importar eventos agora
            </Button>
          )}
        </div>
      </Paper>
    </div>
  );
};

export default GoogleCalendar;
